<template>
  <div class="mission-card-section" :class="`color-type-${mission.colorType}`">
    <div class="mission-card-section__default" v-if="!mission.hasCompleted">
      <div class="mission-card-section__header">
        <p class="mission-card-section__title">
          <slot name="header-icon"></slot>
          <span>{{ mission.missionName }}</span>
        </p>
        <div class="mission-card-section__header-info">
          <p class="mission-card-section__description">{{ mission.description }}</p>
          <p class="mission-card-section__point" v-if="mission.nextAchievementPoint">
            +{{ mission.nextAchievementPoint }}P
          </p>
        </div>
      </div>
      <div
        class="mission-card-section__body"
        :class="{ 'mission-card-section__body-no-space': noSpace }"
      >
        <slot></slot>
      </div>
      <details v-if="displayFooter" class="mission-card-section__footer">
        <summary class="mission-card-section__accordion">
          <span>{{ $tc('MissionPage.howTo') }}</span>
          <mission-accordion-icon class="mission-card-section__accordion-icon" />
        </summary>
        <div class="mission-card-section__how">
          <slot name="howto"></slot>
        </div>
      </details>
    </div>
    <div class="mission-card-section__complete" v-else>
      <p class="mission-card-section__complete-title">
        <slot name="header-icon"></slot>
        <span ref="missionName" :class="{ 'is-wide': state.isMissionNameWide }">{{
          mission.missionName
        }}</span>
      </p>
      <div class="mission-card-section__complete-body">
        <slot></slot>
      </div>
      <div class="mission-card-section__complete-message">
        <mission-complete-icon />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
  watch,
} from '@vue/composition-api'
import MissionAccordionIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionAccordionIcon.vue'
import MissionCompleteIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionCompleteIcon.vue'
import { MissionProgressCardType } from '@/components/MissionPage/hook/useMissionProgressData'

/**
 * ミッション画面 ミッション達成状況セクション
 */
export default defineComponent({
  name: 'MissionCardSection',
  components: {
    MissionCompleteIcon,
    MissionAccordionIcon,
  },
  props: {
    mission: {
      type: Object as PropType<MissionProgressCardType>,
      required: true,
    },
    noSpace: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const state = reactive({
      isMissionNameWide: false,
    })
    const missionName = ref<HTMLElement | null>(null)
    const manageMissionNameWidth = () => {
      const missionNameWidth: number = missionName.value?.getBoundingClientRect().width || 0
      if (missionNameWidth > 300) {
        state.isMissionNameWide = true
      }
    }
    /**
     * フッターを表示するかどうか
     */
    const displayFooter = computed(() => {
      if (props.mission.missionGroupCode === 'CHECKED_IN_BONUS') {
        return !!(
          props.mission.achievementMethod &&
          props.mission.checkPointList &&
          props.mission.checkPointList.length > 0
        )
      }
      return !!props.mission.achievementMethod
    })
    onMounted(() => {
      manageMissionNameWidth()
    })
    watch(
      () => props.mission,
      () => {
        manageMissionNameWidth()
      },
    )
    return {
      state,
      missionName,
      displayFooter,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-card-section {
  &.color-type-1 {
    .mission-card-section__default {
      background-color: rgba(#2293fa, 0.15);
    }

    .mission-card-section__point {
      color: #73ffdd;
    }

    .mission-card-section__attention {
      color: #73ffdd;
      background-color: rgba(#73ffdd, 0.2);
    }

    .mission-card-section__attention-mark {
      border-bottom-color: rgba(#73ffdd, 0.2);
    }

    .mission-card-section__get-point {
      &.is-completed {
        color: #55ffd6;
      }
    }

    .mission-card-section__complete-title {
      color: #b8f9e9;
    }

    .mission-card-section__complete-message {
      color: #55ffd6;
    }

    .mission-card-section__complete {
      background-image: url('~@/assets/img/MissionPage/bg_type01.png');
    }
  }

  &.color-type-2 {
    .mission-card-section__default {
      background-color: rgba(#08edc4, 0.12);
    }

    .mission-card-section__point {
      color: #e0ffa0;
    }

    .mission-card-section__attention {
      color: #e0ffa0;
      background-color: rgba(#e0ffa0, 0.2);
    }

    .mission-card-section__attention-mark {
      border-bottom-color: rgba(#e0ffa0, 0.2);
    }

    .mission-card-section__get-point {
      &.is-completed {
        color: #d1ff72;
      }
    }

    .mission-card-section__complete-title {
      color: #e0ffa0;
    }

    .mission-card-section__complete-message {
      color: #d1ff72;
    }

    .mission-card-section__complete {
      background-image: url('~@/assets/img/MissionPage/bg_type02.png');
    }
  }

  &.color-type-3 {
    .mission-card-section__default {
      background-color: rgba(#7536f9, 0.12);
    }

    .mission-card-section__point {
      color: #f7c6ff;
    }

    .mission-card-section__attention {
      color: #f7c6ff;
      background-color: rgba(#f7c6ff, 0.2);
    }

    .mission-card-section__attention-mark {
      border-bottom-color: rgba(#f7c6ff, 0.2);
    }

    .mission-card-section__get-point {
      &.is-completed {
        color: #ee8ffd;
      }
    }

    .mission-card-section__complete-title {
      color: #f7c6ff;
    }

    .mission-card-section__complete-message {
      color: #ee8ffd;
    }

    .mission-card-section__complete {
      background-image: url('~@/assets/img/MissionPage/bg_type03.png');
    }
  }

  &.color-type-4 {
    .mission-card-section__default {
      background-color: rgba(#ff3216, 0.08);
    }

    .mission-card-section__point {
      color: #ff5d5d;
    }

    .mission-card-section__attention {
      color: #ff5d5d;
      background-color: rgba(#ff5d5d, 0.2);
    }

    .mission-card-section__attention-mark {
      border-bottom-color: rgba(#ff5d5d, 0.2);
    }

    .mission-card-section__get-point {
      &.is-completed {
        color: #ff5d5d;
      }
    }

    .mission-card-section__complete-title {
      color: #ff5d5d;
    }

    .mission-card-section__complete-message {
      color: #ff5d5d;
    }

    .mission-card-section__complete {
      background-image: url('~@/assets/img/MissionPage/bg_type04.png');
    }
  }

  &__default {
    border: 1px solid rgba(#fff, 0.2);
    border-radius: 16px;
    backdrop-filter: blur(15px);
  }

  &__header {
    padding: 12px 15px;
    background-color: $white10;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }

  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.06em;
    color: $white;
    line-height: 1.25;

    svg {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
    }
  }

  &__title-icon {
    display: flex;
    margin-right: 8px;
  }

  &__header-info {
    position: relative;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }

  &__description {
    padding-left: 26px;
    font-size: 11px;
    color: $white70;
    letter-spacing: 0.02em;
    line-height: 1.36;
  }

  &__point {
    font-size: 14px;
    font-weight: 500;
  }

  &__body {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 24px;
    padding: 16px 0 24px;

    &.mission-card-section__body-no-space {
      gap: 0;
      padding-top: 0;
      padding-bottom: 0;

      .mission-card-section__get-point {
        padding-top: 12px;
        padding-bottom: 12px;
      }
    }
  }

  &__process {
    width: 100%;
    padding: 0 15px;
  }

  &__footer {
    background-color: $white10;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;

    > summary {
      list-style: none;

      &::marker,
      &::-webkit-details-marker {
        display: none;
      }
    }

    &[open] {
      .mission-card-section__accordion-icon {
        transform: rotate(180deg);
      }
    }
  }

  &__accordion {
    display: flex;
    align-items: center;
    width: 100%;
    height: 42px;
    padding-left: 16px;
    font-size: 14px;
    color: $white;
  }

  &__accordion-icon {
    margin-left: 5px;
  }

  &__how {
    padding: 20px 14px;
    background-color: $black10;
  }

  &__how-title {
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.06em;
    line-height: 1.25;
  }

  &__how-description {
    margin-top: 16px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 1.67;
  }

  &__how-info {
    margin-top: 16px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  &__how-info-link {
    border-bottom: 1px solid $white70;

    svg {
      margin-left: 2px;
      transform: translateY(-3px);
    }
  }

  &__how-note {
    margin-top: 16px;
    font-size: 11px;
    color: $white70;
    letter-spacing: 0.02em;
    line-height: 1.36;
  }

  &__complete {
    position: relative;
    display: flex;
    gap: 6px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 220px;
    overflow: hidden;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border: 1px solid rgba(#fff, 0.2);
    border-radius: 16px;
  }

  &__complete-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.06em;

    svg {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
    }

    .is-wide {
      width: 200px;
    }
  }

  &__complete-message {
    position: absolute;
    top: 52%;
    left: -1px;
    width: calc(100% + 2px);
    color: #ee8ffd;
    transform: translateY(-50%);
  }

  &__complete-body {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 6px;

    .mission-card-get-point {
      &.is-dark {
        background-color: transparent;
      }
    }
  }

  &__check-point {
    margin-top: 16px;

    &-title {
      font-size: 16px;
      font-weight: 500;
      color: #73ffdd;
      letter-spacing: 0.06em;
    }

    &-area {
      font-size: 14px;
      margin-top: 16px;
      font-weight: 700;
      color: #73ffdd;
      opacity: 0.75;
    }

    &-item {
      position: relative;
      display: flex;
      align-items: center;
      font-size: 12px;
      color: #73ffdd;
      letter-spacing: 0.02em;
    }

    &-area-name {
      font-weight: 600;
    }

    &-mission-term {
      font-weight: 600;
      opacity: 0.75;
    }

    &-icon {
      position: absolute;
      top: 50%;
      left: 0;
      width: 18px;
      height: 18px;
      transform: translateY(-50%);
    }

    &-map {
      width: 18px;
      height: 18px;
      margin-left: auto;
    }
  }

  @media screen and (min-width: $responsiveTabletWidth) {
    &__complete {
      gap: 16px;
      height: auto;
      aspect-ratio: 600 / 381;
    }

    &__complete-title {
      font-size: 18px;

      .is-wide {
        width: auto;
      }
    }

    &__complete-body {
      gap: 16px;
    }
  }
}
</style>
